import PropTypes from 'prop-types';
import React, {Fragment, Component} from 'react';
import {useRouteMatch, useLocation, Link} from 'react-router-dom';
import styled from 'styled-components';
import {colors} from '@atlaskit/theme';
import {
  ContainerHeader,
  GroupHeading,
  HeaderSection,
  Item,
  ItemAvatar,
  LayoutManager,
  MenuSection,
  NavigationProvider,
  GlobalNav,
  GlobalItem,
  modeGenerator,
  ThemeProvider,
  SkeletonContainerView,
  GlobalNavigationSkeleton,
} from '@atlaskit/navigation-next';
import {
  DropdownItem,
  DropdownItemGroup,
  DropdownMenuStateless,
} from '@atlaskit/dropdown-menu';

import Drawer from '@atlaskit/drawer';
import Icon from '@atlaskit/icon';
import FolderIcon from '@atlaskit/icon/glyph/folder';
import BillingIcon from '@atlaskit/icon/glyph/creditcard';
import HomeIcon from '@atlaskit/icon/glyph/home';
import SignOutIcon from '@atlaskit/icon/glyph/sign-out';
import Avatar from '@atlaskit/avatar';
import SearchIcon from '@atlaskit/icon/glyph/search';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import IssuesIcon from '@atlaskit/icon/glyph/issues';

import QueuesIcon from '@atlaskit/icon/glyph/queues';
import InfoIcon from '@atlaskit/icon/glyph/info';

import SearchDrawer from '../components/SearchDrawer';

import dealerLogo from '../images/dealer.svg';

import {
  LadaLogoIcon, 
  SystemIcon, 
  CarIcon, 
  DiagnosticIcon,
} from './Icons';
import {scrollTo} from '../helpers/navigate';
import {Trans} from 'react-i18next';

const NavItem = ({link}) => {
  const [url, title, Icon] = link;
  const match = useRouteMatch({path: url});
  const {hash} = useLocation();
  const isActive = hash ? url === hash : match;
  return (
    <Item
      styles={(base) => isActive ? ({
        ...base,
        textWrapper: {
          ...base.textWrapper,
          color: '#FF5630',
        },
        beforeWrapper: {
          ...base.beforeWrapper,
          color: '#FF5630',
        },
        itemBase: {
          ...base.itemBase,
          backgroundColor: 'rgba(9, 30, 66, 0.04)',
        }
      }) : {...base}
      }
      before={Icon}
      text={<Trans>{title}</Trans>}
      isActive={isActive}
      component={({children, className}) => (
        <Link className={className} to={url}>
          {children}
        </Link>
      )}
    />
  );
}

const ClientNavItem = ({link}) => {
  const [url, title, Icon] = link;
  const isActive = false;
  return (
    <Item
      styles={(base) => isActive ? ({
        ...base,
        textWrapper: {
          ...base.textWrapper,
          color: '#FF5630',
        },
        beforeWrapper: {
          ...base.beforeWrapper,
          color: '#FF5630',
        },
        itemBase: {
          ...base.itemBase,
          backgroundColor: 'rgba(9, 30, 66, 0.04)',
        }
      }) : {...base}
      }
      before={Icon}
      text={<Trans>{title}</Trans>}
      isActive={isActive}
      onClick={() => scrollTo(url)}
    />
  );
}

const ContainerNavigation = (state, router, userProfile, clientProfile) => {
  const match = useRouteMatch({path: '/clients/:id', exact: true});

  if (match && match.params && match.params.id !== 'new') {
    if (!clientProfile.data || (!clientProfile.data && clientProfile.fetching)) {
      return <SkeletonContainerView type='container'/>;
    } else {
      return ClientContainerNavigation(state, router, userProfile, clientProfile.data);
    }
  }
  return MainContainerNavigation(state, router, userProfile);
}

const mainMenu = {
  navLinks: [
    ['/clients', 'Clients', FolderIcon, 'clients'],
    ['/billing', 'Billing', BillingIcon, 'billing'],
    ['/notifications', 'Campaigns', IssuesIcon, 'mailings'],
    ['/reports', 'Reports', GraphLineIcon, 'reports'],
  ],
  adminNavLinks: [
    ['/diagnostic', 'Diagnostics', DiagnosticIcon, 'diagnostic'],
    ['/dealers', 'Dealers', HomeIcon, 'dealers'],
    ['/users', 'Users', PeopleGroupIcon, 'users'],
  ],
  clientNavLinks: [
    ['client', 'Client', InfoIcon],
    ['cars', 'Vehicle', CarIcon],
    ['system', 'System', SystemIcon],
    ['billing', 'Billing', BillingIcon],
    ['note', 'Note', QueuesIcon],
  ],
  fota: [
    ['/fotaCars', 'Cars', CarIcon, 'campaigns'],
    ['/firmwares', 'Firmwares', DiagnosticIcon, 'firmwares'],
    ['/campaigns', 'FotaCampaigns', SystemIcon, 'campaigns']
  ]
};

const MainContainerNavigation = (state, router, userProfile) => (
  <Fragment>
    <HeaderSection isActive={false}>
      {({css}) => (
        <div style={{...css}}>
          <ContainerHeader
            isFocused={false}
            isActive={false}
            isHover={false}
            before={itemState => (
              <ItemAvatar
                itemState={itemState}
                appearance="square"
                size="large"
                src={dealerLogo}
                alt="lada logo"
              />
            )}
            text={userProfile.dealer && userProfile.dealer.name}
            subText={userProfile.dealer && userProfile.dealer.city && userProfile.dealer.city.name}
          />
        </div>
      )}
    </HeaderSection>
    <MenuSection>
        {({className}) => (
          <div className={className}>
            {mainMenu.navLinks.map((link, index) => userProfile.permissions[link[3]] && (<NavItem key={`navLink_${index}`} link={link} />), this)}
            {(userProfile.permissions.dealers || userProfile.permissions.users || userProfile.permissions.diagnostic) && (
              <Fragment>
                <GroupHeading><Trans>Administration</Trans></GroupHeading>
                {mainMenu.adminNavLinks.map((link, index) => userProfile.permissions[link[3]] && (<NavItem key={`adminNavLink_${index}`} link={link} />), this)}
              </Fragment>
            )}
            {(userProfile.permissions.firmwares || userProfile.permissions.campaigns || userProfile.permissions.campaigns) && 
              <Fragment>
              <GroupHeading>FOTA</GroupHeading>
              {mainMenu.fota.map((link, index) =>  userProfile.permissions[link[3]]?.view && (<NavItem key={`fotaLink_${index}`} link={link} />), this)}
              </Fragment>
            }
          </div>
        )}
    </MenuSection>
  </Fragment>
);

const ClientContainerNavigation = (state, router, userProfile, client) => (
  <Fragment>
    <HeaderSection>
      {({css}) => {
        const strIndex = client.fullName.indexOf(' ');

        return (
        <div style={{...css }}>
          <ContainerHeader
            isFocused={false}
            isActive={false}
            isHover={false}
            before={itemState => (
              <ItemAvatar
                itemState={itemState}
                size='large'
                alt='client avatar'
              />
            )}
            
            text={client.fullName && strIndex >= 0 && client.fullName.substring(0, strIndex)}
            subText={client.fullName && strIndex >= 0 && client.fullName.substring(strIndex)}
          />
        </div>
      )}}
    </HeaderSection>
    <MenuSection>
        {({className}) => (
          <div className={className}>
            { 
              mainMenu.clientNavLinks.map((link, index) => {
                return (
                  <ClientNavItem key={`clientNavLink_${index}`} link={link} />
                );
              }, this)
            }
          </div>
        )}
    </MenuSection>
  </Fragment>
);

const customThemeMode = modeGenerator({
  product: {
    text: colors.B50,
    background: colors.N800,
  },
});

class GlobalItemWithDropdown extends Component {
  state = {
    isOpen: false,
  };

  handleOpenChange = ({isOpen}) => this.setState({isOpen});

  render() {
    const {items, trigger: Trigger} = this.props;
    const {isOpen} = this.state;
    return (
      <DropdownMenuStateless
        boundariesElement='window'
        isOpen={isOpen}
        onOpenChange={this.handleOpenChange}
        position='top left'
        trigger={<Trigger isOpen={isOpen} />}
      >
        {items}
      </DropdownMenuStateless>
    );
  }
}

const ItemComponent = ({dropdownItems: DropdownItems, ...itemProps}) => {
  if (DropdownItems) {
    return (
      <GlobalItemWithDropdown
        trigger={({isOpen}) => (
          <GlobalItem isSelected={isOpen} {...itemProps} />
        )}
        items={<DropdownItems />}
      />
    );
  }
  return <GlobalItem {...itemProps} />;
};

const VersionWrapper = styled.span`
  font-size: 10px;
  color: #6B778C;
  padding-left: 12px;
  line-height: 16px;
`;

const ProfileDropdown = ({fullName, signOut}) => (
  <Fragment>
    <DropdownItemGroup title={fullName}>
      <DropdownItem
        onClick={signOut}
        elemBefore={<SignOutIcon size='small'/>}
      >
        <Trans>Logout</Trans>
      </DropdownItem>
    </DropdownItemGroup>
    <VersionWrapper>
      <Trans>Ver</Trans> {process.env.REACT_APP_APP_VERSION}
    </VersionWrapper>
  </Fragment>
);

const LanguageDropdown = ({currentLang, langs, changeLang}) => (
  <DropdownItemGroup>
    {langs.map(l => (
      <DropdownItem
        key={`lang_item_${l.id}`}
        onClick={() => changeLang(l.id)}
        isSelected={currentLang === l.id}
      >
        {l.title}
      </DropdownItem>
    ))}
  </DropdownItemGroup>
);

const CurrentLangWrapper = styled.span`
  font-size: 10px;
  color: #6B778C;
  padding-left: 12px;
  line-height: 16px;
  margin: 0;
  padding: 0;
`;

const globalNavigation = (
  openDrawer,
  fullName,
  signOut,
  currentLang,
  langs,
  changeLang,
) => () => (
  <GlobalNav
    itemComponent={ItemComponent}
    primaryItems={[
      {
        id: 'global_nav_logo',
        href:'/',
        icon: LadaLogoIcon
      },
      {
        id: 'global_nav_search',
        icon: SearchIcon,
        onClick: openDrawer('search')
      },
    ]}
    secondaryItems={[
      {
        id: 'global_nav_lang',
        icon: () => <Icon size='small' glyph={() => <CurrentLangWrapper><Trans>{currentLang}</Trans></CurrentLangWrapper>}/>,
        //icon: () => <img width='18px' height='18px' src="en.png"/>,//() => LangIcon(currentLang),
        dropdownItems: () => (<LanguageDropdown currentLang={currentLang} langs={langs} changeLang={changeLang} />),
      },
      {
        id: 'global_nav_help',
        href:'https://help.ladaconnect.com/',
        target: '_blank',
        icon: QuestionCircleIcon
      },
      {
        id: 'global_nav_avatar',
        dropdownItems: () => (<ProfileDropdown fullName={fullName} signOut={signOut}/>),
        icon: () => (
          <Avatar
            borderColor='transparent'
            isActive={false}
            isHover={false}
          />
        ),
      },
    ]}
  />
)

const SidebarOverrideComponent = ({children}) => {
  return (
    <div
      style={{
        alignItems: 'center',
        boxSizing: 'border-box',
        display: 'flex',
        flexShrink: 0,
        flexDirection: 'column',
        height: '100vh',
        paddingBottom: 16,
        paddingTop: 24,
        width: 64,
      }}
    >
      <div
        style={{
          height: 40,
          width: 40,
        }}
      >
        <LadaLogoIcon primaryColor={colors.N500} />
      </div>
      {children}
    </div>
  );
};

const renderSkeleton = () => 
  <SkeletonContainerView type='container'/>;

export default class StarterNavigation extends React.Component {
  state = {
    openDrawer: null,
  };

  static contextTypes = {
    navOpenState: PropTypes.object,
    router: PropTypes.object
  };

  openDrawer = openDrawer => () => {
    this.setState({openDrawer});
  };

  render () {
    const {
      userProfile,
      clientProfile,
      signOut,
      currentLang,
      langs,
      changeLang,
    } = this.props;
    return (
      <div>
        <NavigationProvider>
          <ThemeProvider
            theme={theme => ({
              ...theme,
              mode: customThemeMode,
            })}
          >
            <LayoutManager
              globalNavigation={
                userProfile.userRole ?
                  globalNavigation(this.openDrawer, userProfile.fullName, signOut, currentLang, langs, changeLang)
                  : () => <GlobalNavigationSkeleton />
              }
              productNavigation={() => null}
              containerNavigation={
                userProfile.userRole ? 
                  () => ContainerNavigation(this.state, this.context.router, userProfile, clientProfile)
                  : renderSkeleton
              }
            />
          </ThemeProvider>
        </NavigationProvider>
        <Drawer
          onClose={this.openDrawer(null)}
          onCloseComplete={this.onCloseComplete}
          onOpenComplete={this.onOpenComplete}
          isOpen={this.state.openDrawer === 'search'}
          width='wide'
          overrides={{
            Sidebar: {
              component: SidebarOverrideComponent,
            },
          }}
        >
          <SearchDrawer
            {...this.props.search}
            onSearch={this.props.getSearch}
            onResultClicked={this.openDrawer(null)}
          />
        </Drawer>
      </div>
    )
  }
}